import { Body2 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { ReportStatus } from '@/modules/Report/Report.schema';
import { BookOpenText, Hourglass, Loader, TriangleAlert } from 'lucide-react';
import { ReactNode } from 'react';

type NonCompletedReportStatus = Exclude<ReportStatus, 'completed'>;

interface NoPdfProps {
  status: NonCompletedReportStatus;
  generatePdf: () => void;
  generatingPdf: boolean;
}

const statusIcons: Record<NonCompletedReportStatus, ReactNode> = {
  pending: <BookOpenText />,
  queued: <Hourglass />,
  in_progress: <Loader className="animate-spin" />,
  failed: <TriangleAlert />,
};

const getStatusMessage = (status: NonCompletedReportStatus, regeneratePdf: () => void, generatingPdf: boolean): ReactNode => {
  const ClickHere = (text: string) => (
    <button
      onClick={regeneratePdf}
      disabled={generatingPdf}
      className={cn('text-gray-600 underline hover:text-gray-800 focus:outline-none')}
    >
      {text}
    </button>
  );

  const messages: Record<NonCompletedReportStatus, ReactNode> = {
    pending: <>A report for this Farm does not yet exist. {ClickHere('Click here')} to generate one.</>,
    queued: <>Your report is in the queue. (If you need to force regeneration {ClickHere('click here')}.)</>,
    in_progress: <>Your report is currently being generated. Please check back shortly.</>,
    failed: <>There was an error generating your report. {ClickHere('Click here')} to try again.</>,
  };

  return messages[status];
};

export const NoPdf = ({ status, generatePdf, generatingPdf }: NoPdfProps) => {
  return (
    <div className={cn('flex items-center justify-center m-12')}>
      <div className={cn('flex flex-col items-center gap-2 bg-secondary-15 py-6 px-6')}>
        {statusIcons[status]}
        <Body2 className={cn('p-2')}>{getStatusMessage(status, generatePdf, generatingPdf)}</Body2>
      </div>
    </div>
  );
};
